import { CellInfo } from 'components/cell-visualizations/tsv/types'
import {
  combineMorphotypePoints,
  getHiddenCellInfoGroupValues,
  getOriginalPlotSelectionState,
} from 'components/cell-visualizations/utils'
import _ from 'lodash'
import { useMemo } from 'react'
import { getNewUniqueName, getRandomId } from 'utils/helpers'
import { cellVisualizationsSlice } from '../CellVisualizationsSlice'
import {
  CellInfoDimension,
  CellInfoGroup,
  CellInfoGroupNone,
  CellInfoGroups,
  PaginationParams,
  PinnedMorphotype,
  PlotSelectionState,
} from '../types'
import useEventsManager from './useEventsManager'
import usePlotlySlice from './usePlotlySlice'
import useSliceWrapper from './useSliceWrapper'

const useGetMethodsAndData = () => {
  const rawSliceMethodsAndData = useSliceWrapper('cellVisualizations', cellVisualizationsSlice)
  const { resetRange } = usePlotlySlice()

  const sliceMethodsAndData = rawSliceMethodsAndData

  const {
    setShowMorphotypes,
    setPinnedCells,
    setPinnedGroupValue,
    setCellInfoGroups,
    setStore,
    cellVisualizations: {
      fileName,
      pinnedCells,
      dataFrame, // Use singleton DataFrame from Redux (prevents 100+ duplicate instances)
      cellInfoGroups,
      selectedCellInfoGroupName,
      selectedComparisonDimensions,
      mergedPinnedCells,
      morphotypesToRemove,
    },
  } = sliceMethodsAndData

  const eventsManager = useEventsManager()

  const prefilteredCellIdSet = useMemo(
    () =>
      new Set<string>(
        morphotypesToRemove?.flatMap((m) => m.cells?.points?.flatMap((p) => `${p.cellId}`) ?? [])
      ),
    [morphotypesToRemove]
  )

  const byVisiblePinnedCells = useMemo(
    () =>
      <T extends Pick<PinnedMorphotype, 'cells'>>(pc: T) =>
        ({
          ...pc,
          cells: {
            ...pc.cells,
            points: pc.cells.points?.filter(({ cellId }) =>
              cellId ? !prefilteredCellIdSet.has(cellId) : true
            ),
          },
        } as T),
    [prefilteredCellIdSet]
  )

  const visiblePinnedCells = useMemo(
    () => pinnedCells?.map(byVisiblePinnedCells),
    [byVisiblePinnedCells, pinnedCells]
  )

  const visibleMergedPinnedCells = useMemo(
    () => mergedPinnedCells?.map(byVisiblePinnedCells),
    [byVisiblePinnedCells, mergedPinnedCells]
  )

  // need to use this ternary because it's undefined in tests for some reason
  const selectedCellInfoGroup = cellInfoGroups
    ? cellInfoGroups[selectedCellInfoGroupName ?? ''] ?? CellInfoGroupNone
    : CellInfoGroupNone

  const { hiddenCellInfoGroupValues, hiddenValuesExist } = useMemo(() => {
    const vals = getHiddenCellInfoGroupValues(cellInfoGroups)
    return { hiddenCellInfoGroupValues: vals, hiddenValuesExist: !!_.keys(vals).length }
  }, [cellInfoGroups])

  const visibleSelectedComparisonDimensions = useMemo(() => {
    const visible: CellInfoDimension[] | undefined = selectedComparisonDimensions?.flatMap(
      (scd) => ({
        ...scd,
        values: scd.values.filter((v) =>
          hiddenCellInfoGroupValues[scd.field]
            ? !hiddenCellInfoGroupValues[scd.field]?.includes(v)
            : true
        ),
      })
    )
    return visible
  }, [hiddenCellInfoGroupValues, selectedComparisonDimensions])

  // Create cell ID index for fast lookups using DataFrame
  const cellIdIndex = useMemo(() => {
    if (!dataFrame) return new Map<string, number>()
    const cellIdCol = dataFrame.column('CELL_ID')
    if (!cellIdCol || !cellIdCol.toArray) return new Map<string, number>()
    const cellIds = cellIdCol.toArray() as string[]
    const index = new Map<string, number>()
    cellIds.forEach((id, idx) => index.set(id, idx))
    return index
  }, [dataFrame])

  // NOTE: Removed columnArraysCache to save ~500-800 MB memory
  // getCellInfoByCellId now extracts values on-demand using DataFrame.get()

  const helperMethods = useMemo(() => {
    return {
      clearCellData: () => {
        resetRange()
        setStore({})
      },
      setPointsAsSelected: (currentSelection?: PlotSelectionState) => {
        if (currentSelection) {
          /*
                  there's a bug?/feature? in plotly where if the layout.shapes
                  and/or layout.annotations are updated then the onSelected gets
                  triggered with e.points and e.selections as empty arrays
                  */
          const { points, selections, lassoPoints, range } = currentSelection
          if (points && points.length > 0 && selections && selections.length > 0) {
            // don't include points that have been filtered

            setShowMorphotypes(true)
            if (lassoPoints) {
              eventsManager.sendLassoEvent(fileName as string, points.length)
            } else if (range) {
              eventsManager.sendRectangleEvent(fileName as string, points.length)
            }

            const currentSelectionState = {
              ...currentSelection,
              // @TODO Figure out why we need to make a copy of the points.  It doesn't work otherwise?
              points: points?.map((point) => ({
                x: point.x,
                y: point.y,
                originalX: point.originalX,
                originalY: point.originalY,
                cellId: point.cellId,
              })),
              selections: selections?.map((selection) => ({
                type: selection.type,
                x0: selection.x0,
                y0: selection.y0,
                x1: selection.x1,
                y1: selection.y1,
                path: selection.path,
              })),
            } as PlotSelectionState

            const newPinnedCells = [
              // Place the new selection first, so that the user is more likely to see it
              // @TODO Scroll to the new selection in the future
              {
                // default is 1000 random ids, which should be enough...
                id: getRandomId({ exclude: pinnedCells?.map((x) => x.id) }),
                name: getNewUniqueName(pinnedCells),
                cells: getOriginalPlotSelectionState(currentSelection),
                transposedCells: currentSelectionState,
                active: true,
                breakdownType: 'RUN_ID', // Default breakdown type
                // pagination: statePagination,
                comparisonDimensionsSnapshot: selectedComparisonDimensions,
                cellInfoGroupsSnapshot: hiddenValuesExist
                  ? _.keys(cellInfoGroups).reduce((acc, k) => {
                      const key = k as keyof typeof cellInfoGroups
                      if (hiddenCellInfoGroupValues[key]) {
                        return { ...acc, [key]: cellInfoGroups[key] }
                      }
                      return acc
                    }, {} as CellInfoGroups)
                  : undefined,
              } as PinnedMorphotype,
              ...(pinnedCells ?? []),
            ]
            setPinnedCells({ pinnedCells: newPinnedCells, eventsManager })
          }

        }
      },
      getCellInfoByCellId: (cellId: string): CellInfo => {
        if (!dataFrame) return {} as CellInfo
        const rowIndex = cellIdIndex.get(cellId)
        if (rowIndex === undefined) return {} as CellInfo

        // Extract row data on-demand by extracting columns only when needed
        // This is slower than cache but saves ~500-800 MB of memory
        // Extract column arrays and index - only called occasionally for tooltips/lookups
        const cellInfo: Partial<CellInfo> = { CELL_ID: cellId }
        const columnNames = dataFrame.columnNames()
        columnNames.forEach((colName) => {
          const col = dataFrame.column(colName)
          if (col && col.toArray) {
            // Cast to array to allow indexing
            const colArray = col.toArray() as Array<string | number | boolean | null | undefined>
            // Dynamically assign column values - value type depends on column
            cellInfo[colName as keyof CellInfo] = colArray[rowIndex] as CellInfo[keyof CellInfo]
          }
        })

        return cellInfo as CellInfo
      },

      setCellInfoGroupData: (key: keyof CellInfoGroups, data: CellInfoGroup['data']) => {
        setCellInfoGroups({ ...cellInfoGroups, [key]: { ...cellInfoGroups[key], data } })
      },

      setSelectedCellInfoGroupData: (data: CellInfoGroup['data']) => {
        setCellInfoGroups({
          ...cellInfoGroups,
          [selectedCellInfoGroupName]: { ...cellInfoGroups[selectedCellInfoGroupName], data },
        })
      },
      getMergedPinnedMorphotypes: () => {
        // First, try to get merged points from mergedPinnedCells
        const mergedPointsMap = new Map<number, NonNullable<PinnedMorphotype['cells']['points']>>()
        mergedPinnedCells?.forEach((merged) => {
          if (merged.cells.points) {
            mergedPointsMap.set(merged.id, merged.cells.points)
          }
        })

        return (
          pinnedCells?.reduce((acc, pinnedMorphotype) => {
            const existingPinnedMorphotypeIndex = acc.findIndex((x) => x.id === pinnedMorphotype.id)
            if (existingPinnedMorphotypeIndex > -1) {
              return acc.map((accPinnedMorphotype, i) => {
                if (i === existingPinnedMorphotypeIndex) {
                  // Try to get merged points first
                  const mergedPoints = mergedPointsMap.get(accPinnedMorphotype.id)
                  if (mergedPoints) {
                    return {
                      ...accPinnedMorphotype,
                      cells: {
                        ...accPinnedMorphotype.cells,
                        points: mergedPoints,
                      },
                    }
                  }

                  // Fallback: combine points from all morphotypes with the same ID
                  // This uses the shared utility function to match the logic in MergePinned.tsx handleMerge
                  const uniquePoints = combineMorphotypePoints(
                    pinnedCells,
                    mergedPinnedCells,
                    accPinnedMorphotype.id,
                    { shuffle: true }
                  )

                  return {
                    ...accPinnedMorphotype,
                    cells: {
                      ...accPinnedMorphotype.cells,
                      points: uniquePoints.length > 0 ? uniquePoints : accPinnedMorphotype.cells.points,
                    },
                  }
                }
                return accPinnedMorphotype
              })
            }
            return [...acc, pinnedMorphotype]
          }, [] as PinnedMorphotype[]) ?? []
        )
      },
      setPinnedGroupHighlighted: ({
        pinnedGroupId,
        isHighlighted = true,
      }: {
        pinnedGroupId: number
        isHighlighted?: boolean
      }) => {
        setPinnedGroupValue({
          pinnedGroupId,
          key: 'isHighlighted',
          value: isHighlighted,
        })
      },
      setPinnedGroupPagination: ({
        pinnedGroupId,
        pagination,
      }: {
        pinnedGroupId: number
        pagination: PaginationParams
      }) => {
        setPinnedGroupValue({
          pinnedGroupId,
          key: 'pagination',
          value: pagination,
        })
      },

      updatePinnedName: ({ pinnedGroupId, name }: { pinnedGroupId: number; name: string }) =>
        setPinnedGroupValue({ pinnedGroupId, key: 'name', value: name }),
      setPinnedGroupActive: ({
        pinnedGroupId,
        active,
      }: {
        pinnedGroupId: number
        active?: boolean
      }) =>
        setPinnedGroupValue({
          pinnedGroupId,
          key: 'active',
          value: Boolean(active),
        }),
    }
  }, [cellIdIndex, dataFrame, cellInfoGroups, eventsManager, fileName, hiddenCellInfoGroupValues, hiddenValuesExist, mergedPinnedCells, pinnedCells, resetRange, selectedCellInfoGroupName, selectedComparisonDimensions, setCellInfoGroups, setPinnedCells, setPinnedGroupValue, setShowMorphotypes, setStore])

  return {
    ...sliceMethodsAndData,
    ...helperMethods,
    dataFrame, // Export DataFrame for components to use
    selectedCellInfoGroup,
    hiddenCellInfoGroupValues,
    visibleSelectedComparisonDimensions,
    visiblePinnedCells,
    visibleMergedPinnedCells,
    prefilteredCellIdSet,
  } as const
}

export const useCellVisualizationsSlice = (): ReturnType<typeof useGetMethodsAndData> =>
  useGetMethodsAndData()

export type UseCellVisualizationsSlice = ReturnType<typeof useCellVisualizationsSlice>
